import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { RefreshCw, Calendar, Phone, Mail, Clock, User, Inbox } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-sage-100 p-6 shadow-sm" data-testid={testid}>
    <p className="text-sm text-muted-foreground mb-1">{label}</p>
    <p className="text-3xl font-bold text-primary">{value}</p>
  </div>
);

const AdminDashboard = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAppointments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/appointments`);
      const sorted = [...res.data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setAppointments(sorted);
    } catch (e) {
      setError("Could not load appointments. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const pendingCount = appointments.filter((a) => a.status === "pending").length;

  const formatDateTime = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-foreground">
              Appointment Bookings
            </h1>
            <p className="text-muted-foreground mt-1">
              Neelkanth Dentistry — incoming web bookings
            </p>
          </div>
          <button
            onClick={fetchAppointments}
            className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-full font-medium hover:opacity-90 transition-opacity disabled:opacity-60"
            disabled={loading}
            data-testid="admin-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <StatCard label="Total Bookings" value={appointments.length} testid="admin-stat-total" />
          <StatCard label="Pending" value={pendingCount} testid="admin-stat-pending" />
        </div>

        {/* Content */}
        {error && (
          <div className="bg-red-50 text-red-700 border border-red-200 rounded-xl p-4 mb-6" data-testid="admin-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="admin-loading">
            Loading bookings...
          </div>
        ) : appointments.length === 0 ? (
          <div className="text-center py-20" data-testid="admin-empty">
            <Inbox className="w-12 h-12 text-sage-300 mx-auto mb-3" />
            <p className="text-muted-foreground">No bookings yet.</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-2xl border border-sage-100 shadow-sm overflow-hidden" data-testid="admin-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-sage-50 text-left text-foreground">
                    <th className="px-5 py-3 font-semibold">Patient</th>
                    <th className="px-5 py-3 font-semibold">Contact</th>
                    <th className="px-5 py-3 font-semibold">Service</th>
                    <th className="px-5 py-3 font-semibold">Preferred Slot</th>
                    <th className="px-5 py-3 font-semibold">Message</th>
                    <th className="px-5 py-3 font-semibold">Received</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((a, i) => (
                    <tr
                      key={a.id}
                      className={i % 2 === 0 ? "bg-white" : "bg-sage-50/40"}
                      data-testid={`admin-row-${a.id}`}
                    >
                      <td className="px-5 py-4 font-medium text-foreground">{a.patient_name}</td>
                      <td className="px-5 py-4 text-muted-foreground">
                        <div>{a.phone}</div>
                        {a.email && <div className="text-xs">{a.email}</div>}
                      </td>
                      <td className="px-5 py-4 text-muted-foreground">{a.service}</td>
                      <td className="px-5 py-4 text-muted-foreground">
                        {a.preferred_date} · {a.preferred_time}
                      </td>
                      <td className="px-5 py-4 text-muted-foreground max-w-[200px] truncate">{a.message || "-"}</td>
                      <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{formatDateTime(a.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-4">
              {appointments.map((a) => (
                <motion.div
                  key={a.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-sage-100 p-5 shadow-sm"
                  data-testid={`admin-card-${a.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-primary" />
                    <span className="font-semibold text-foreground">{a.patient_name}</span>
                  </div>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {a.phone}</div>
                    {a.email && <div className="flex items-center gap-2"><Mail className="w-4 h-4" /> {a.email}</div>}
                    <div className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {a.service}</div>
                    <div className="flex items-center gap-2"><Clock className="w-4 h-4" /> {a.preferred_date} · {a.preferred_time}</div>
                    {a.message && <div className="pt-1 italic">"{a.message}"</div>}
                    <div className="text-xs pt-1">Received: {formatDateTime(a.created_at)}</div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
